import { z } from 'zod';
import { TimestampSchema, UuidSchema } from './common';

/**
 * BM (Business Manager) — container Meta que agrupa WABAs, Ad Accounts e
 * Pages. Schemas espelham o snapshot que o backend persiste depois de sincar
 * via Graph API. Plaintext do System User Access Token nunca trafega no
 * contrato HTTP — só `tokenMasked` (últimos 4 chars).
 */

export const BMStatusSchema = z.enum(['active', 'restricted', 'pending', 'error', 'disconnected']);
export type BMStatus = z.infer<typeof BMStatusSchema>;

export const BMVerificationStatusSchema = z.enum([
  'verified',
  'not_verified',
  'pending_need_more_info',
  'unknown',
]);
export type BMVerificationStatus = z.infer<typeof BMVerificationStatusSchema>;

export const QualityRatingSchema = z.enum(['GREEN', 'YELLOW', 'RED', 'UNKNOWN']);
export type QualityRating = z.infer<typeof QualityRatingSchema>;

/**
 * `platform_type` da Graph API — distingue ONDE o número está hospedado:
 * - `CLOUD_API`: registrado na WhatsApp Cloud API (hospedado pela Meta).
 *   É o único sinal confiável de "número registrado" para envio via Cloud.
 * - `ON_PREMISE`: rodando na On-Premises API legada (deprecada pela Meta).
 * - `NOT_APPLICABLE`: número ainda não registrado em nenhuma plataforma.
 * `undefined` quando o snapshot é antigo (sincronizado antes deste campo
 * existir) — a UI trata isso como "registro desconhecido", não como erro.
 */
export const PhoneNumberPlatformTypeSchema = z.enum(['CLOUD_API', 'ON_PREMISE', 'NOT_APPLICABLE']);
export type PhoneNumberPlatformType = z.infer<typeof PhoneNumberPlatformTypeSchema>;

export const PhoneNumberSnapshotSchema = z.object({
  id: z.string(),
  displayPhoneNumber: z.string(),
  verifiedName: z.string(),
  qualityRating: QualityRatingSchema,
  codeVerificationStatus: z.string().optional(),
  /** Ver `PhoneNumberPlatformTypeSchema` — sinal de registro na Cloud API. */
  platformType: PhoneNumberPlatformTypeSchema.optional(),
});
export type PhoneNumberSnapshot = z.infer<typeof PhoneNumberSnapshotSchema>;

export const WabaSnapshotSchema = z.object({
  wabaId: z.string(),
  name: z.string(),
  timezoneId: z.string().optional(),
  messageTemplateNamespace: z.string().optional(),
  qualityRating: QualityRatingSchema,
  phoneNumbers: z.array(PhoneNumberSnapshotSchema),
  linkedConnectionId: UuidSchema.nullable(),
});
export type WabaSnapshot = z.infer<typeof WabaSnapshotSchema>;

export const QualityBreakdownSchema = z.object({
  green: z.number().int().nonnegative(),
  yellow: z.number().int().nonnegative(),
  red: z.number().int().nonnegative(),
  unknown: z.number().int().nonnegative(),
});
export type QualityBreakdown = z.infer<typeof QualityBreakdownSchema>;

export const BMWebhookSubscriptionSchema = z.object({
  field: z.string(),
  subscribed: z.boolean(),
});
export type BMWebhookSubscription = z.infer<typeof BMWebhookSubscriptionSchema>;

/**
 * Shape público do BM. `tokenMasked` mostra só os últimos 4 chars (ex.
 * `••••XYZ4`); plaintext nunca sai do backend.
 */
export const BusinessManagerSchema = z.object({
  id: UuidSchema,
  metaBmId: z.string(),
  name: z.string(),
  nickname: z.string().min(1).max(100).nullable(),
  status: BMStatusSchema,
  verificationStatus: BMVerificationStatusSchema,
  tokenMasked: z.string(),
  qualityBreakdown: QualityBreakdownSchema,
  webhookSubscriptions: z.array(BMWebhookSubscriptionSchema),
  wabaCount: z.number().int().nonnegative(),
  adAccountCount: z.number().int().nonnegative(),
  lastSyncedAt: TimestampSchema.nullable(),
  lastSyncError: z.string().nullable(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type BusinessManager = z.infer<typeof BusinessManagerSchema>;

export const BusinessManagerDetailSchema = BusinessManagerSchema.extend({
  wabas: z.array(WabaSnapshotSchema),
});
export type BusinessManagerDetail = z.infer<typeof BusinessManagerDetailSchema>;

export const BusinessManagersListResponseSchema = z.object({
  businessManagers: z.array(BusinessManagerSchema),
});
export type BusinessManagersListResponse = z.infer<typeof BusinessManagersListResponseSchema>;

/**
 * Connect — recebe um System User Access Token gerado dentro de uma BM no
 * Meta Business Suite. O backend chama `GET /me/businesses` pra resolver a
 * lista de BMs que o token enxerga; pra MVP gravamos a primeira (ou faz
 * connect explícito por `metaBmId` quando o token tem acesso a várias).
 */
export const ConnectBusinessManagerInputSchema = z.object({
  systemUserToken: z.string().min(40),
  metaBmId: z.string().min(1).optional(),
  nickname: z.string().min(1).max(100).optional(),
});
export type ConnectBusinessManagerInput = z.infer<typeof ConnectBusinessManagerInputSchema>;

/**
 * Resposta do connect/reconnect de uma BM.
 *
 * `propagatedConnectionCount` informa quantas connections-irmãs (mesmas
 * `businessManagerId`) tiveram o Access Token atualizado em cascata. É 0 num
 * connect novo ou num reconnect que não trocou o token; > 0 quando o token da
 * BM mudou e foi propagado — a UI usa isso para o toast
 * ("token propagado para N conexões"). Rotacionar o token vira 1 ação.
 */
export const ConnectBusinessManagerResponseSchema = z.object({
  businessManager: BusinessManagerDetailSchema,
  propagatedConnectionCount: z.number().int().nonnegative(),
});
export type ConnectBusinessManagerResponse = z.infer<typeof ConnectBusinessManagerResponseSchema>;

export const UpdateBusinessManagerNicknameInputSchema = z.object({
  nickname: z.string().min(1).max(100).nullable(),
});
export type UpdateBusinessManagerNicknameInput = z.infer<
  typeof UpdateBusinessManagerNicknameInputSchema
>;

export const SyncBusinessManagerResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
  businessManager: BusinessManagerDetailSchema.optional(),
});
export type SyncBusinessManagerResponse = z.infer<typeof SyncBusinessManagerResponseSchema>;

/**
 * Config que o cliente precisa colar no Meta App Dashboard pra receber
 * webhooks. `verifyToken` é sensível (admin-only); plaintext porque é o que
 * o Meta envia no handshake — não dá pra mascarar.
 */
export const BMWebhookConfigSchema = z.object({
  callbackUrl: z.string().url(),
  verifyToken: z.string(),
  recommendedFields: z.array(z.string()),
  metaAppDashboardUrl: z.string().url(),
  hasVerifyToken: z.boolean(),
});
export type BMWebhookConfig = z.infer<typeof BMWebhookConfigSchema>;

export const CheckWabaSubscriptionResponseSchema = z.object({
  subscribed: z.boolean(),
  appCount: z.number().int().nonnegative(),
  message: z.string(),
});
export type CheckWabaSubscriptionResponse = z.infer<typeof CheckWabaSubscriptionResponseSchema>;

/**
 * Register — registra um phone_number da WABA na Cloud API
 * (`POST /{phone-number-id}/register`). O `pin` é o código de verificação em
 * duas etapas: se o número já tem 2SV ativo, precisa ser o PIN existente;
 * senão, este valor passa a ser o novo PIN.
 */
export const RegisterPhoneNumberInputSchema = z.object({
  pin: z.string().regex(/^\d{6}$/, 'PIN deve ter exatamente 6 dígitos'),
});
export type RegisterPhoneNumberInput = z.infer<typeof RegisterPhoneNumberInputSchema>;

export const RegisterPhoneNumberResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
});
export type RegisterPhoneNumberResponse = z.infer<typeof RegisterPhoneNumberResponseSchema>;

/**
 * Status de registro de um número na WhatsApp Cloud API, derivado do
 * `platformType` do snapshot:
 * - `registered`: `platformType === 'CLOUD_API'` — pronto pra enviar.
 * - `not_registered`: `platformType` é `ON_PREMISE` ou `NOT_APPLICABLE` —
 *   precisa rodar o registro (`POST /{id}/register`).
 * - `unknown`: snapshot antigo sem `platformType` — rode o sync da BM pra
 *   obter o sinal. A UI rotula honestamente como "desconhecido".
 */
export const PhoneRegistrationStatusSchema = z.enum(['registered', 'not_registered', 'unknown']);
export type PhoneRegistrationStatus = z.infer<typeof PhoneRegistrationStatusSchema>;

/**
 * Linha da tela "Registros" — um número de telefone achatado com a BM/WABA
 * dona resolvidas. Agrega `wabasSnapshot` de TODAS as BMs do tenant numa
 * lista única, pronta pra tabela. `businessManagerStatus` carrega o status
 * da BM dona (a UI desabilita a ação de registrar se a BM está desconectada).
 */
export const PhoneRegistrationEntrySchema = z.object({
  phoneNumberId: z.string(),
  displayPhoneNumber: z.string(),
  verifiedName: z.string(),
  qualityRating: QualityRatingSchema,
  registrationStatus: PhoneRegistrationStatusSchema,
  platformType: PhoneNumberPlatformTypeSchema.optional(),
  businessManagerId: UuidSchema,
  businessManagerName: z.string(),
  businessManagerStatus: BMStatusSchema,
  wabaId: z.string(),
  wabaName: z.string(),
});
export type PhoneRegistrationEntry = z.infer<typeof PhoneRegistrationEntrySchema>;

export const PhoneRegistrationsListResponseSchema = z.object({
  /** Número total de BMs do tenant — distingue "sem BM" de "BM sem número". */
  businessManagerCount: z.number().int().nonnegative(),
  phoneNumbers: z.array(PhoneRegistrationEntrySchema),
});
export type PhoneRegistrationsListResponse = z.infer<typeof PhoneRegistrationsListResponseSchema>;
